import { useState, useEffect, useRef, useCallback } from 'react';

// Throttle function for batching updates
const throttle = (func, limit) => {
    let inThrottle;
    let lastArgs;
    return function(...args) {
        lastArgs = args;
        if (!inThrottle) {
            func.apply(this, lastArgs);
            inThrottle = true;
            setTimeout(() => {
                inThrottle = false;
                if (lastArgs) {
                    func.apply(this, lastArgs);
                }
            }, limit);
        }
    };
};

// Message state + scroll behaviour for the chat thread.
//
// NOTE: the legacy `sendMessage` streaming path (and its `apiService.sendChatMessageStream`
// backend, which hit `/v1/chat/completions` with NO auth header) was removed in F-5.5 —
// it had zero call sites. All real sending now goes through the agentic RAG stream in
// MainOfficeView. This hook is kept for the pieces MainOfficeView actually consumes:
// `messages`/`setMessages` (the visible-thread buffer) and the scroll-to-bottom logic.
// `isLoading`/`canStop`/`stopStreaming` are retained as stable no-ops so consumers'
// destructuring and props are unchanged (their state was only ever mutated by the now
// deleted `sendMessage`, so runtime behaviour is identical).
export const useChat = () => {
    const [messages, setMessages] = useState([]);
    const [isLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [canStop] = useState(false);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Throttled scroll - only scroll every 100ms during streaming
    const throttledScroll = useRef(throttle(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100)).current;

    // True when the user is already parked near the bottom of the thread. Used to
    // honour "User Priority" scroll-locking: if they've scrolled up to read
    // history, streaming tokens must NOT yank the view back down.
    const isNearBottom = useCallback(() => {
        let el = messagesEndRef.current?.parentElement;
        while (el) {
            const oy = window.getComputedStyle(el).overflowY;
            if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) break;
            el = el.parentElement;
        }
        if (!el) return true; // can't find a scroller → preserve scroll-to-bottom
        return el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    }, []);

    const prevLenRef = useRef(0);
    useEffect(() => {
        const countChanged = messages.length !== prevLenRef.current;
        prevLenRef.current = messages.length;
        // A new message or a freshly-loaded/switched conversation (count changed) —
        // always land at the bottom. Streaming tokens grow the LAST message without
        // changing the count, so there we only follow if the user is near the bottom.
        if (countChanged) {
            scrollToBottom();
        } else if (isNearBottom()) {
            if (isLoading) throttledScroll(); else scrollToBottom();
        }
    }, [messages, isLoading, throttledScroll, scrollToBottom, isNearBottom]);

    const stopStreaming = () => {};

    return {
        messages,
        isLoading,
        messagesEndRef,
        setMessages,
        canStop,
        stopStreaming
    };
};
